import { Link } from 'react-router-dom';
import { Settings2, ArrowRight } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Banner } from '@reflex/ui/components/shared/Banner';
import { cn } from '@reflex/ui/lib/utils';
import type { OnboardingProgress } from '@reflex/shared';

export interface RequiredConfigurationBannerProps {
  /** Progress summary to display. */
  progress: OnboardingProgress;
  /** Whether there are outstanding steps (incomplete or skipped). */
  hasOutstanding: boolean;
  /** Link to the onboarding configuration page. */
  href?: string;
  /** Callback when user clicks the dismiss button. */
  onDismiss?: () => void;
}

export function RequiredConfigurationBanner({
  progress,
  hasOutstanding,
  href = '/onboard',
  onDismiss,
}: RequiredConfigurationBannerProps) {
  if (!hasOutstanding) return null;

  const outstanding = progress.total - progress.completed;

  return (
    <Banner
      data-testid="required-config-banner"
      intent="warning"
      icon={Settings2}
      className="items-center"
      action={
        <div className="flex items-center gap-1.5">
          {onDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss} aria-label="Dismiss">
              Dismiss
            </Button>
          )}
          <Button asChild size="sm" aria-label="Complete setup">
            <Link to={href}>
              Complete setup
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3">
        <p className="text-sm font-medium text-foreground">Finish setting up your workspace</p>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1"
            role="progressbar"
            aria-valuenow={progress.completed}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-label="Configuration progress"
          >
            {Array.from({ length: progress.total }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  i < progress.completed ? 'bg-status-accent-orange' : 'bg-status-accent-orange/25',
                )}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {outstanding} {outstanding === 1 ? 'step' : 'steps'} remaining
          </p>
        </div>
      </div>
    </Banner>
  );
}
